import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIndicatorIcon,
  Close as CloseIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
import ContentTrackingService from './backendservices/ContentTrackingService';
import {
  Checklist,
  CreateChecklistRequest,
  UpdateChecklistRequest,
} from '../types/ContentTracking';

interface ChecklistManagementProps {
  category: 'Blog' | 'Linkedin Post';
  onShowSnackbar: (message: string, severity: 'success' | 'error') => void;
}

const SortableRow = ({ children, ...props }: any) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : 0,
    backgroundColor: isDragging ? '#2b2c3e' : '#1e1e2f', // Match card bg
    position: 'relative' as const,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      {...props}
      sx={{
        '& td': { borderBottom: 'none' },
        '& td:first-of-type': {
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          borderLeft: '4px solid #fdd835', // Yellow accent border
        },
        '& td:last-of-type': {
          borderTopRightRadius: '12px',
          borderBottomRightRadius: '12px',
        },
        mb: 2, // Spacing between rows (handled by table spacing)
        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.2)',
      }}
    >
      <TableCell
        {...attributes}
        {...listeners}
        sx={{ width: '50px', cursor: 'grab', touchAction: 'none', color: '#8f90a6' }}
      >
        <DragIndicatorIcon />
      </TableCell>
      {children}
    </TableRow>
  );
};

const ChecklistManagement: React.FC<ChecklistManagementProps> = ({ category, onShowSnackbar }) => {
  const userRole = Cookies.get('role');
  const isSales = userRole === 'sales';
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [editingChecklist, setEditingChecklist] = useState<Checklist | null>(null);
  const [checklistDialogMode, setChecklistDialogMode] = useState<'create' | 'edit'>('create');
  const [openChecklistFormDialog, setOpenChecklistFormDialog] = useState(false);
  const [checklistFormData, setChecklistFormData] = useState<CreateChecklistRequest>({
    title: '',
    description: '',
    status: 'active',
    category: category,
  });
  const [loadingChecklists, setLoadingChecklists] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [checklistToDelete, setChecklistToDelete] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingChecklist, setViewingChecklist] = useState<Checklist | null>(null);
  const [totalChecklists, setTotalChecklists] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldChecklists = [...checklists];
    const oldIndex = checklists.findIndex((item) => item._id === active.id);
    const newIndex = checklists.findIndex((item) => item._id === over.id);
    const newChecklists = arrayMove(checklists, oldIndex, newIndex);

    setChecklists(newChecklists);

    try {
      const orderedIds = newChecklists.map((item) => item._id);
      const response = await ContentTrackingService.reorderChecklists(orderedIds);
      if (!response.status) {
        throw new Error(response.message || 'Failed to reorder');
      }
    } catch (err) {
      console.error('Failed to reorder checklists:', err);
      onShowSnackbar('Failed to reorder checklists', 'error');
      setChecklists(oldChecklists);
    }
  };

  useEffect(() => {
    fetchChecklists();
    resetChecklistForm();
    setOpenChecklistFormDialog(false);
  }, [category, page, rowsPerPage]);

  const fetchChecklists = async () => {
    try {
      setLoadingChecklists(true);
      const response = await ContentTrackingService.getActiveChecklistsByCategory(
        category,
        page + 1,
        rowsPerPage
      );
      if (response.status) {
        setChecklists(response.data);
        setTotalChecklists(response.total || 0);
      }
    } catch (error) {
      console.error('Error fetching checklists:', error);
      onShowSnackbar('Failed to fetch checklists', 'error');
    } finally {
      setLoadingChecklists(false);
    }
  };

  const handleCreateChecklist = async () => {
    const trimmedTitle = checklistFormData.title.trim();
    const trimmedDescription = checklistFormData.description.trim();

    if (!trimmedTitle || !trimmedDescription) {
      onShowSnackbar('Title and description are required', 'error');
      return;
    }

    if (trimmedTitle.length < 3 || trimmedDescription.length < 10) {
      onShowSnackbar(
        'Title must be at least 3 characters and description at least 10 characters',
        'error'
      );
      return;
    }

    try {
      const validatedData = {
        ...checklistFormData,
        title: trimmedTitle,
        description: trimmedDescription,
        category: category,
      };

      const response = await ContentTrackingService.createChecklist(validatedData);
      if (response.status) {
        onShowSnackbar('Checklist created successfully', 'success');
        resetChecklistForm();
        setOpenChecklistFormDialog(false);
        fetchChecklists();
      }
    } catch (error) {
      console.error('Error creating checklist:', error);
      onShowSnackbar('Failed to create checklist', 'error');
    }
  };

  const handleUpdateChecklist = async () => {
    if (!editingChecklist) return;

    const trimmedTitle = checklistFormData.title.trim();
    const trimmedDescription = checklistFormData.description.trim();

    if (!trimmedTitle || !trimmedDescription) {
      onShowSnackbar('Title and description are required', 'error');
      return;
    }

    try {
      const updateData: UpdateChecklistRequest = {
        title: trimmedTitle,
        description: trimmedDescription,
        status: checklistFormData.status,
        category: category,
      };

      const response = await ContentTrackingService.updateChecklistItem(
        editingChecklist._id,
        updateData
      );
      if (response.status) {
        onShowSnackbar('Checklist updated successfully', 'success');
        resetChecklistForm();
        setOpenChecklistFormDialog(false);
        fetchChecklists();
      }
    } catch (error) {
      console.error('Error updating checklist:', error);
      onShowSnackbar('Failed to update checklist', 'error');
    }
  };

  const handleDeleteClick = (checklistId: string) => {
    setChecklistToDelete(checklistId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!checklistToDelete) return;

    try {
      const response = await ContentTrackingService.deleteChecklist(checklistToDelete);
      if (response.status) {
        onShowSnackbar('Checklist deleted successfully', 'success');
        fetchChecklists();
        setDeleteDialogOpen(false);
        setChecklistToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting checklist:', error);
      onShowSnackbar('Failed to delete checklist', 'error');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setChecklistToDelete(null);
  };

  const handleEditChecklist = (checklist: Checklist) => {
    setEditingChecklist(checklist);
    setChecklistDialogMode('edit');
    setChecklistFormData({
      title: checklist.title,
      description: checklist.description,
      status: checklist.status,
      category: checklist.category,
    });
    setOpenChecklistFormDialog(true);
  };

  const resetChecklistForm = () => {
    setChecklistFormData({
      title: '',
      description: '',
      status: 'active',
      category: category,
    });
    setEditingChecklist(null);
    setChecklistDialogMode('create');
  };

  const handleCloseChecklistFormDialog = () => {
    setOpenChecklistFormDialog(false);
    resetChecklistForm();
  };

  const formatDate = (dateString: string) => {
    try {
      return dayjs(dateString).format('MMM DD, YYYY');
    } catch {
      return dateString;
    }
  };

  const handleViewChecklist = (checklist: Checklist) => {
    setViewingChecklist(checklist);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setViewingChecklist(null);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleChecklists = checklists;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant='h4' sx={{ fontWeight: 700, color: '#6871d1' }}>
          Blog Checklist
        </Typography>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          disabled={isSales}
          onClick={() => {
            resetChecklistForm();
            setChecklistDialogMode('create');
            setOpenChecklistFormDialog(true);
          }}
          sx={{
            backgroundColor: '#6871d1',
            color: '#fff',
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1rem',
            padding: '10px 24px',
            '&:hover': {
              backgroundColor: '#5a63bb',
            },
            '&:disabled': {
              backgroundColor: '#aeb3e9',
              color: '#fff',
            },
          }}
        >
          Create Checklist
        </Button>
      </Box>

      {/* Checklists Table */}
      {loadingChecklists ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: '#6871d1' }} />
        </Box>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <Box>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant='h6' sx={{ color: '#fff' }}>
                Total Checklists
              </Typography>
              <Chip
                label={`Total: ${totalChecklists}`}
                size='small'
                sx={{ bgcolor: '#6871d1', color: '#fff' }}
              />
            </Box>
            <TableContainer component={Box} sx={{ overflow: 'visible' }}>
              <Table sx={{ borderCollapse: 'separate', borderSpacing: '0 16px' }}>
                <TableHead>
                  <TableRow>
                    <TableCell width='50px' />
                    <TableCell
                      sx={{
                        color: '#8f90a6',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}
                    >
                      Checklist Details
                    </TableCell>
                    <TableCell
                      sx={{
                        color: '#8f90a6',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}
                    >
                      Category
                    </TableCell>
                    <TableCell
                      sx={{
                        color: '#8f90a6',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}
                      align='right'
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <SortableContext
                    items={visibleChecklists.map((c) => c._id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {visibleChecklists.map((checklist) => (
                      <SortableRow key={checklist._id} data-row-key={checklist._id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography
                              variant='subtitle1'
                              sx={{
                                fontWeight: 600,
                                color: '#fff',
                                cursor: 'pointer',
                                '&:hover': { color: '#6871d1' },
                              }}
                              onClick={() => handleViewChecklist(checklist)}
                            >
                              {checklist.title}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: '#ffb74d',
                                cursor: 'pointer',
                              }}
                              onClick={() => handleViewChecklist(checklist)}
                            >
                              <CalendarTodayIcon sx={{ fontSize: 14 }} />
                              <Typography variant='caption' sx={{ fontWeight: 500 }}>
                                {formatDate(checklist.createdAt)}
                              </Typography>
                            </Box>
                            <Typography
                              variant='caption'
                              sx={{
                                color: '#8f90a6',
                                mt: 0.5,
                                display: 'block',
                                maxWidth: '300px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {checklist.description}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={category}
                            size='small'
                            sx={{
                              bgcolor: '#2c2d3c',
                              color: '#ddd',
                              border: '1px solid #444',
                              borderRadius: 1,
                            }}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Tooltip title='Edit'>
                              <IconButton
                                size='small'
                                onClick={() => handleEditChecklist(checklist)}
                                sx={{ color: '#8f90a6', '&:hover': { color: '#fff' } }}
                              >
                                <EditIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                            {!isSales && (
                              <Tooltip title='Delete'>
                                <IconButton
                                  size='small'
                                  onClick={() => handleDeleteClick(checklist._id)}
                                  sx={{ color: '#8f90a6', '&:hover': { color: '#f44336' } }}
                                >
                                  <DeleteIcon fontSize='small' />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </SortableRow>
                    ))}
                  </SortableContext>
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component='div'
              count={totalChecklists}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                color: '#fff',
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  color: '#8f90a6',
                },
                '.MuiTablePagination-select': {
                  color: '#fff',
                },
                '.MuiTablePagination-actions': {
                  color: '#fff',
                },
              }}
            />
          </Box>
        </DndContext>
      )}

      {/* Checklist Form Dialog */}
      <Dialog
        open={openChecklistFormDialog}
        onClose={handleCloseChecklistFormDialog}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#2b2c3e',
            color: '#fff',
            borderRadius: 3,
            boxShadow: '0 8px 32px 0 rgba(0,0,0,0.6)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: '#23234a', // Slightly darker header
            color: '#fff',
            fontWeight: 600,
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 2.5,
            px: 3,
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(104, 113, 209, 0.2)',
              p: 0.5,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6871d1',
            }}
          >
            {checklistDialogMode === 'create' ? (
              <AddIcon fontSize='small' />
            ) : (
              <EditIcon fontSize='small' />
            )}
          </Box>
          {checklistDialogMode === 'create' ? 'Create New Checklist' : 'Edit Checklist'}
        </DialogTitle>
        <DialogContent sx={{ background: '#2b2c3e', px: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box>
              <Typography
                variant='caption'
                sx={{ color: '#8f90a6', mb: 1, display: 'block', fontWeight: 500 }}
              >
                Title *
              </Typography>
              <TextField
                placeholder='Enter task title'
                value={checklistFormData.title}
                onChange={(e) =>
                  setChecklistFormData({ ...checklistFormData, title: e.target.value })
                }
                fullWidth
                variant='outlined'
                InputProps={{
                  sx: {
                    bgcolor: '#1e1e2f',
                    color: '#fff',
                    borderRadius: 2,
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                    height: '48px',
                  },
                }}
                required
              />
            </Box>

            <Box>
              <Typography
                variant='caption'
                sx={{ color: '#8f90a6', mb: 1, display: 'block', fontWeight: 500 }}
              >
                Category
              </Typography>
              <TextField
                value={category}
                fullWidth
                disabled
                variant='outlined'
                InputProps={{
                  sx: {
                    bgcolor: '#1e1e2f',
                    color: '#fff',
                    borderRadius: 2,
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                    '&.Mui-disabled': {
                      bgcolor: '#1e1e2f',
                      color: '#aaaaaa',
                      '-webkit-text-fill-color': '#aaaaaa',
                    },
                  },
                }}
              />
            </Box>

            <Box>
              <Typography
                variant='caption'
                sx={{ color: '#8f90a6', mb: 1, display: 'block', fontWeight: 500 }}
              >
                Content *
              </Typography>
              <TextField
                placeholder='Enter task description...'
                value={checklistFormData.description}
                onChange={(e) =>
                  setChecklistFormData({ ...checklistFormData, description: e.target.value })
                }
                fullWidth
                multiline
                rows={4}
                required
                InputProps={{
                  sx: {
                    bgcolor: '#1e1e2f',
                    color: '#fff',
                    borderRadius: 2,
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                    '&.Mui-focused fieldset': { borderColor: '#6871d1' },
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ background: '#23234a', p: 3 }}>
          <Button
            onClick={handleCloseChecklistFormDialog}
            sx={{
              backgroundColor: 'transparent',
              color: '#fff',
              border: '1px solid #6871d1',
              py: 1,
              borderRadius: '8px',
              fontWeight: 500,
              transition: 'all 0.3s ease',
              px: 4,
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: 'rgba(104,113,209,0.1)',
                border: '1px solid #6871d1',
              },
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={() =>
              checklistDialogMode === 'create' ? handleCreateChecklist() : handleUpdateChecklist()
            }
            variant='contained'
            disabled={!checklistFormData.title || !checklistFormData.description}
            sx={{
              backgroundColor: '#6871d1',
              color: '#fff',
              py: 1,
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(104, 113, 209, 0.4)',
              transition: 'all 0.3s ease',
              fontWeight: 500,
              px: 4,
              fontSize: '0.875rem',
              '&:hover': {
                backgroundColor: '#5a63bb',
                boxShadow: '0 6px 16px rgba(104, 113, 209, 0.6)',
              },
              '&:disabled': {
                backgroundColor: '#aeb3e9',
                color: '#fff',
                opacity: 0.7,
              },
            }}
          >
            {checklistDialogMode === 'create' ? 'CREATE' : 'UPDATE'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Checklist Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#2b2c3e',
            color: '#fff',
            borderRadius: 3,
            boxShadow: '0 8px 32px 0 rgba(0,0,0,0.6)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: '#23234a',
            color: '#fff',
            fontWeight: 600,
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 2.5,
            px: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                bgcolor: 'rgba(104, 113, 209, 0.2)',
                p: 0.5,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6871d1',
              }}
            >
              <AssignmentIcon fontSize='small' />
            </Box>
            Checklist Details
          </Box>
          <IconButton onClick={handleCloseViewDialog} sx={{ color: '#8f90a6', p: 0.5 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ background: '#2b2c3e', px: 3, pt: 3, pb: 5 }}>
          {viewingChecklist && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant='caption' sx={{ color: '#8f90a6', mb: 0.5, display: 'block' }}>
                  Title
                </Typography>
                <Typography variant='h6' sx={{ color: '#fff', fontWeight: 600 }}>
                  {viewingChecklist.title}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box>
                  <Typography
                    variant='caption'
                    sx={{ color: '#8f90a6', mb: 0.5, display: 'block' }}
                  >
                    Category
                  </Typography>
                  <Chip
                    label={viewingChecklist.category}
                    size='small'
                    sx={{
                      bgcolor: '#1e1e2f',
                      color: '#ddd',
                      border: '1px solid #444',
                      borderRadius: 1,
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant='caption'
                    sx={{ color: '#8f90a6', mb: 0.5, display: 'block' }}
                  >
                    Date
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#ffb74d' }}>
                    <CalendarTodayIcon sx={{ fontSize: 16 }} />
                    <Typography variant='body2'>
                      {formatDate(viewingChecklist.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box>
                <Typography variant='caption' sx={{ color: '#8f90a6', mb: 1, display: 'block' }}>
                  Description
                </Typography>
                <Box
                  sx={{
                    bgcolor: '#1e1e2f',
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    maxHeight: '300px',
                    overflowY: 'auto',
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{ color: '#ddd', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
                  >
                    {viewingChecklist.description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ background: '#23234a', p: 3 }} />
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#1e1e2f',
            color: '#fff',
            borderRadius: 4,
            boxShadow: '0 20px 60px 0 rgba(0,0,0,0.9)',
            border: '1px solid rgba(244, 67, 54, 0.2)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: '#23234a',
            color: '#fff',
            fontWeight: 700,
            fontSize: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 2.5,
            px: 3,
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            m: 0,
          }}
        >
          <Box
            sx={{
              p: 1.25,
              borderRadius: 2,
              backgroundColor: 'rgba(244, 67, 54, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(244, 67, 54, 0.3)',
            }}
          >
            <DeleteIcon sx={{ color: '#f44336', fontSize: 22 }} />
          </Box>
          <Typography
            variant='h6'
            sx={{
              color: '#fff',
              fontWeight: 700,
              fontSize: '1.25rem',
              letterSpacing: 0.3,
            }}
          >
            Delete Checklist
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ background: '#1e1e2f', pt: 3.5, pb: 2, px: 3 }}>
          <Typography
            variant='body1'
            sx={{
              color: '#fff',
              fontSize: '0.95rem',
              lineHeight: 1.6,
              fontWeight: 400,
              mb: 2.5,
            }}
          >
            Are you sure you want to delete this checklist? This action cannot be undone and all
            associated data will be permanently removed.
          </Typography>
          <Box
            sx={{
              p: 2,
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              borderRadius: 2,
              border: '1px solid rgba(244, 67, 54, 0.2)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 4,
                height: 4,
                borderRadius: '50%',
                backgroundColor: '#f44336',
                flexShrink: 0,
              }}
            />
            <Typography
              variant='body2'
              sx={{
                color: '#ffcdd2',
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              This action is permanent and cannot be reversed
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            background: '#23234a',
            p: 2.5,
            gap: 1.5,
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            m: 0,
          }}
        >
          <Button
            onClick={handleDeleteCancel}
            sx={{
              backgroundColor: 'transparent',
              color: '#aaa',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              py: 1.25,
              borderRadius: '8px',
              fontWeight: 500,
              transition: 'all 0.2s ease',
              px: 3,
              fontSize: '0.95rem',
              textTransform: 'none',
              minWidth: 100,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant='contained'
            startIcon={<DeleteIcon />}
            sx={{
              backgroundColor: '#f44336',
              color: '#fff',
              py: 1.25,
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
              transition: 'all 0.2s ease',
              fontWeight: 600,
              px: 3,
              fontSize: '0.95rem',
              textTransform: 'none',
              minWidth: 120,
              '&:hover': {
                backgroundColor: '#d32f2f',
                boxShadow: '0 4px 12px rgba(244, 67, 54, 0.5)',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChecklistManagement;
